import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SalesReturnPreviewPanel({
  companyId,
  saleId,
  accessToken,
  canExecute = false,
  onReturned,
}) {
  const [candidate, setCandidate] = useState(null),
    [quantities, setQuantities] = useState({}),
    [date, setDate] = useState(""),
    [reason, setReason] = useState(""),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [cashConfirmed, setCashConfirmed] = useState(false),
    [stockConfirmed, setStockConfirmed] = useState(false),
    [posted, setPosted] = useState(null);
  const generation = useRef(0);
  const requestKey = useRef(null);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const url = `/accounting/${companyId}/sales-corrections/pos/${saleId}`;
  function change(setter, value) {
    generation.current++;
    setter(value);
    setPreview(null);
    setError("");
    setCashConfirmed(false);
    setStockConfirmed(false);
    requestKey.current = null;
  }
  async function load() {
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setPreview(null);
    setCandidate(null);
    setQuantities({});
    setCashConfirmed(false);
    setStockConfirmed(false);
    requestKey.current = null;
    try {
      const r = await apiRequest(url + "/return-candidate", { accessToken });
      if (g === generation.current) setCandidate(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  const selected = Object.entries(quantities)
    .filter(([, q]) => q !== "")
    .map(([saleItemId, quantity]) => ({ saleItemId, quantity }));
  const ready =
    !!candidate &&
    ["NO_RECORDED_RETURNS", "LINKED_RETURN_HISTORY"].includes(
      candidate.historyStatus,
    ) &&
    selected.length > 0 &&
    selected.every(
      (x) =>
        /^\d{1,14}(\.\d{1,3})?$/.test(x.quantity) && /[1-9]/.test(x.quantity),
    );
  async function review(e) {
    e.preventDefault();
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setPreview(null);
    setCashConfirmed(false);
    setStockConfirmed(false);
    requestKey.current = null;
    try {
      const r = await apiRequest(url + "/return-preview", {
        accessToken,
        method: "POST",
        body: { date, reason, items: selected },
      });
      if (g === generation.current) setPreview(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  async function execute() {
    const g = ++generation.current;
    setBusy(true);
    setError("");
    try {
      if (!requestKey.current) {
        if (!window.crypto?.randomUUID)
          throw Error("A secure HTTPS or localhost connection is required.");
        requestKey.current = window.crypto.randomUUID();
      }
      const r = await apiRequest(url + "/return", {
        accessToken,
        method: "POST",
        body: {
          date,
          reason,
          items: selected,
          previewHash: preview.previewHash,
          requestKey: requestKey.current,
          cashReturnedConfirmed: cashConfirmed,
          stockReceivedConfirmed: stockConfirmed,
        },
      });
      if (g !== generation.current) return;
      setPosted(r);
      setPreview(null);
      setCashConfirmed(false);
      setStockConfirmed(false);
      setQuantities({});
      if (onReturned) {
        onReturned();
        return;
      }
      const current = await apiRequest(url + "/return-candidate", {
        accessToken,
      });
      if (g === generation.current) setCandidate(current);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  return (
    <section className="mt-4 space-y-4 rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="font-semibold text-slate-900">
        Partial cash return preview
      </h3>
      <p className="text-sm text-slate-600">
        Select original items and quantities to review proposed refunds, GST
        reversal and stock cost. Preview changes nothing; cash returns use your
        current open till in the original store, including older receipts.
        Closed original tills stay unchanged. Returns require a separate
        confirmed action. Non-cash, loyalty, coupon and commission cases remain
        outside the initial scope.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={load}
        className="rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-40"
      >
        {candidate ? "Refresh returnable items" : "Load returnable items"}
      </button>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {posted && (
        <p
          role="status"
          className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold"
        >
          Cash return posted · ₹{posted.refund} · Voucher{" "}
          {posted.voucher.number}. Linked history and original records retained.
        </p>
      )}
      {candidate && (
        <>
          <p className="text-sm">
            {candidate.receiptNumber} · Original date {candidate.originalDate} ·{" "}
            {candidate.originalVoucherNumber}
          </p>
          {candidate.history?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <caption className="text-left font-semibold">
                  Posted return history
                </caption>
                <thead>
                  <tr>
                    <th>Sequence</th>
                    <th>Date</th>
                    <th>Voucher</th>
                    <th>Refund ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {candidate.history.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td>{h.sequence}</td>
                      <td>{h.date}</td>
                      <td>{h.voucherNumber}</td>
                      <td>{h.refund}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!["NO_RECORDED_RETURNS", "LINKED_RETURN_HISTORY"].includes(
            candidate.historyStatus,
          ) ? (
            <p className="rounded-lg bg-amber-50 p-3 text-sm">
              Return selection blocked:{" "}
              {candidate.historyStatus.replaceAll("_", " ")}.{" "}
              {candidate.historyStatus === "FULLY_RETURNED"
                ? "All original quantities have been returned."
                : "Remaining quantities must be reconciled; they are not inferred from logs."}
            </p>
          ) : (
            <form onSubmit={review} className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Original item</th>
                      <th>Original quantity</th>
                      <th>Available</th>
                      <th>Return quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidate.items.map((i) => (
                      <tr key={i.saleItemId} className="border-t">
                        <td className="py-3">
                          {i.productName}
                          <span className="block text-xs text-slate-500">
                            {i.sku}
                          </span>
                        </td>
                        <td>{i.originalQuantity}</td>
                        <td>{i.remainingQuantity ?? "Needs review"}</td>
                        <td>
                          <input
                            aria-label={`Return quantity for ${i.sku}`}
                            inputMode="decimal"
                            disabled={
                              busy || !/[1-9]/.test(i.remainingQuantity || "")
                            }
                            value={quantities[i.saleItemId] || ""}
                            onChange={(e) =>
                              change(setQuantities, {
                                ...quantities,
                                [i.saleItemId]: e.target.value,
                              })
                            }
                            placeholder="Leave blank to skip"
                            className="w-40 rounded-lg border p-2"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="block text-sm font-medium">
                Proposed return date (India)
                <input
                  type="date"
                  required
                  disabled={busy}
                  value={date}
                  onChange={(e) => change(setDate, e.target.value)}
                  className="mt-1 block rounded-lg border p-2"
                />
              </label>
              <label className="block text-sm font-medium">
                Partial return reason
                <textarea
                  required
                  minLength={20}
                  maxLength={500}
                  disabled={busy}
                  value={reason}
                  onChange={(e) => change(setReason, e.target.value)}
                  className="mt-1 w-full rounded-lg border p-3"
                />
              </label>
              <button
                type="submit"
                disabled={busy || !ready || !date || reason.trim().length < 20}
                className="rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-40"
              >
                {busy ? "Reviewing…" : "Preview selected return (no changes)"}
              </button>
            </form>
          )}
        </>
      )}
      {preview && (
        <div className="space-y-4 text-sm">
          <p className="rounded-lg bg-amber-50 p-3 font-semibold">
            {preview.executionEnabled
              ? "Preview only — no changes have been made."
              : "Partial-return execution disabled — no changes have been made."}
          </p>
          {preview.payload.eligibilityBlockers.length > 0 && (
            <ul className="list-disc pl-5">
              {preview.payload.eligibilityBlockers.map((b) => (
                <li key={b}>{b.replaceAll("_", " ")}</li>
              ))}
            </ul>
          )}
          <p>
            Proposed cash refund ₹{preview.payload.totals.refund} · Net revenue
            reversal ₹{preview.payload.totals.net} · Original stock cost ₹
            {preview.payload.totals.cost}
          </p>
          {preview.payload.sessionId && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950">
              Refund from {preview.payload.counterName || "current counter"} ·
              Session{" "}
              {preview.payload.sessionNumber || preview.payload.sessionId}
              {" · "}Posting date {preview.payload.date}. Original sale date{" "}
              {preview.payload.originalDate}. The original closed till is not
              reopened.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Selected original lines — proposed only
              </caption>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Refund ₹</th>
                  <th>CGST ₹</th>
                  <th>SGST ₹</th>
                  <th>IGST ₹</th>
                  <th>Cost ₹</th>
                </tr>
              </thead>
              <tbody>
                {preview.payload.lines.map((l) => (
                  <tr key={l.saleItemId} className="border-t">
                    <td className="py-2">{l.sku}</td>
                    <td>{l.quantity}</td>
                    <td>{l.refund}</td>
                    <td>{l.cgst}</td>
                    <td>{l.sgst}</td>
                    <td>{l.igst}</td>
                    <td>{l.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Proposed accounting voucher — not posted
              </caption>
              <thead>
                <tr>
                  <th>Original ledger</th>
                  <th>Debit ₹</th>
                  <th>Credit ₹</th>
                </tr>
              </thead>
              <tbody>
                {preview.payload.voucherLines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-2">
                      {preview.ledgers.find((x) => x.id === l.ledgerId)?.name ||
                        l.ledgerId}
                    </td>
                    <td>{l.debit}</td>
                    <td>{l.credit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Original-lot restoration — not performed
              </caption>
              <thead>
                <tr>
                  <th>Lot</th>
                  <th>Quantity</th>
                  <th>Original unit cost ₹</th>
                  <th>Cost reversal ₹</th>
                </tr>
              </thead>
              <tbody>
                {preview.payload.restoration.map((r) => (
                  <tr key={r.allocationId} className="border-t">
                    <td className="py-2">{r.lotNumber}</td>
                    <td>{r.quantity}</td>
                    <td>{r.originalUnitCost}</td>
                    <td>{r.originalCost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">
            Cumulative rounding preserves the original refund, GST components
            and cost at final return. Numeric allocation needs accounting
            review; it is not statutory credit-note approval.
          </p>
          {preview.executionEnabled && canExecute && (
            <div className="space-y-3 rounded-xl border border-red-200 p-4">
              <h4 className="font-semibold">Confirm selected cash return</h4>
              <p>
                This records cash you physically returned; it does not send
                money electronically. Return documents, stock, till cash and
                accounting will commit together.
              </p>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={cashConfirmed}
                  disabled={busy}
                  onChange={(e) => setCashConfirmed(e.target.checked)}
                />
                I have returned the exact proposed cash amount to the customer.
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={stockConfirmed}
                  disabled={busy}
                  onChange={(e) => setStockConfirmed(e.target.checked)}
                />
                I have received and verified the selected goods and quantities.
              </label>
              <button
                type="button"
                disabled={busy || !cashConfirmed || !stockConfirmed}
                onClick={execute}
                className="rounded-lg bg-red-700 px-4 py-3 font-semibold text-white disabled:opacity-40"
              >
                {busy ? "Posting…" : "Confirm selected cash return"}
              </button>
            </div>
          )}
          {preview.executionEnabled && !canExecute && (
            <p>
              Execution requires both accounting.vouchers_post and pos.return
              permissions.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
